<template>
  <Teleport to="body">
    <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50" @click.self="emit('close')">
      <div data-tour="lesson-details-modal" class="bg-surface dark:bg-surface-dark rounded-xl shadow-xl w-full max-w-sm mx-4 border border-stroke dark:border-stroke-dark">

        <!-- Header -->
        <div class="flex items-start justify-between px-5 pt-5 pb-4">
          <div>
            <h3 class="text-base font-semibold text-fg-base dark:text-fg-base-dark leading-snug">{{ lesson.lesson_title }}</h3>
            <p v-if="lesson.category" class="text-xs text-fg-muted dark:text-fg-muted-dark mt-0.5">{{ lesson.category }}</p>
          </div>
          <button @click="emit('close')" data-tour="lesson-details-close-btn" class="ml-3 mt-0.5 p-1 text-fg-faint dark:text-fg-faint-dark rounded transition-colors flex-shrink-0">
            <XMarkIcon class="h-5 w-5" />
          </button>
        </div>

        <div class="px-5 pb-5">

          <!-- Info rows -->
          <div class="space-y-2.5">
            <div class="flex items-center gap-3 text-sm">
              <CalendarIcon class="h-4 w-4 text-fg-faint dark:text-fg-faint-dark flex-shrink-0" />
              <span class="text-fg-secondary dark:text-fg-secondary-dark">{{ formatDate(lesson.lesson_date) }} · {{ lesson.lesson_time }}</span>
            </div>
            <div class="flex items-center gap-3 text-sm">
              <ClockIcon class="h-4 w-4 text-fg-faint dark:text-fg-faint-dark flex-shrink-0" />
              <span class="text-fg-secondary dark:text-fg-secondary-dark">{{ lesson.duration_minutes }} min</span>
            </div>
            <div v-if="lesson.instrument" class="flex items-center gap-3 text-sm">
              <MusicalNoteIcon class="h-4 w-4 text-fg-faint dark:text-fg-faint-dark flex-shrink-0" />
              <span class="text-fg-secondary dark:text-fg-secondary-dark">{{ lesson.instrument }}</span>
            </div>
            <div v-if="lesson.teacher_name" class="flex items-center gap-3 text-sm">
              <UserIcon class="h-4 w-4 text-fg-faint dark:text-fg-faint-dark flex-shrink-0" />
              <span class="text-fg-secondary dark:text-fg-secondary-dark">{{ lesson.teacher_name }}</span>
            </div>
            <div v-if="lesson.location" class="flex items-center gap-3 text-sm">
              <MapPinIcon class="h-4 w-4 text-fg-faint dark:text-fg-faint-dark flex-shrink-0" />
              <span class="text-fg-secondary dark:text-fg-secondary-dark">{{ lesson.location }}</span>
            </div>
          </div>

          <!-- Students -->
          <div v-if="students.length" class="mt-4 pt-4 border-t border-stroke dark:border-stroke-dark">
            <p class="text-[11px] font-semibold text-fg-faint dark:text-fg-faint-dark uppercase tracking-wider mb-3">Students</p>
            <div class="space-y-2.5">
              <div v-for="s in students" :key="s.name" class="flex items-center justify-between gap-2">
                <div class="flex items-center gap-2.5 min-w-0">
                  <div class="h-8 w-8 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-semibold"
                       :style="{ background: avatarGradient(s.name) }">
                    {{ initials(s.name) }}
                  </div>
                  <span class="text-sm text-fg-base dark:text-fg-base-dark truncate">{{ s.name }}</span>
                  <AbsenceCourtesyBadge v-if="courtesyFor(s)" :lesson-id="lesson.id" :info="courtesyFor(s)"
                                        compact @updated="courtesies = $event" />
                </div>

                <!-- Status badge / editable dropdown -->
                <div class="relative flex-shrink-0" data-attendance-dropdown>
                  <!-- Editable: clickable badge -->
                  <button v-if="isEditable"
                          @click="toggleDropdown(s.name)"
                          class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-opacity"
                          :class="statusClass(localStatus(s))">
                    {{ formatStatus(localStatus(s)) }}
                    <ChevronDownIcon class="h-3 w-3 opacity-60" />
                  </button>
                  <!-- Read-only: plain badge -->
                  <span v-else
                        class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                        :class="statusClass(localStatus(s))">
                    {{ formatStatus(localStatus(s)) }}
                  </span>

                  <!-- Dropdown -->
                  <div v-if="openDropdown === s.name"
                       class="absolute right-0 top-full mt-1 w-32 rounded-lg border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-dark shadow-lg z-10 overflow-hidden">
                    <button v-for="opt in statusOptions" :key="opt.value ?? 'null'"
                            @click="selectStatus(s.name, opt.value)"
                            class="w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors"
                            :class="localStatus(s) === opt.value
                              ? 'bg-surface-muted dark:bg-surface-muted-dark font-semibold text-fg-base dark:text-fg-base-dark'
                              : 'text-fg-secondary dark:text-fg-secondary-dark'">
                      <span class="h-2 w-2 rounded-full flex-shrink-0" :class="opt.dot" />
                      {{ opt.label }}
                    </button>
                  </div>
                </div>

              </div>
            </div>
          </div>

          <!-- Primary contact -->
          <div v-if="primaryContacts.length" class="mt-4 pt-4 border-t border-stroke dark:border-stroke-dark">
            <p class="text-[11px] font-semibold text-fg-faint dark:text-fg-faint-dark uppercase tracking-wider mb-3">Primary Contact</p>
            <div class="space-y-3">
              <div v-for="c in primaryContacts" :key="c.contact_email || c.contact_phone || c.contact_name" class="space-y-1">
                <div class="flex items-center gap-3 text-sm">
                  <UserIcon class="h-4 w-4 text-fg-faint dark:text-fg-faint-dark flex-shrink-0" />
                  <span class="text-fg-base dark:text-fg-base-dark font-medium">{{ c.contact_name }}</span>
                </div>
                <div v-if="c.contact_phone" class="flex items-center gap-3 text-sm">
                  <PhoneIcon class="h-4 w-4 text-fg-faint dark:text-fg-faint-dark flex-shrink-0" />
                  <a :href="`tel:${c.contact_phone}`" class="text-fg-secondary dark:text-fg-secondary-dark hover:text-blue-600 dark:hover:text-blue-400 transition-colors">{{ c.contact_phone }}</a>
                </div>
                <div v-if="c.contact_email" class="flex items-center gap-3 text-sm">
                  <EnvelopeIcon class="h-4 w-4 text-fg-faint dark:text-fg-faint-dark flex-shrink-0" />
                  <a :href="`mailto:${c.contact_email}`" class="text-fg-secondary dark:text-fg-secondary-dark hover:text-blue-600 dark:hover:text-blue-400 transition-colors truncate">{{ c.contact_email }}</a>
                </div>
              </div>
            </div>
          </div>

          <!-- Homework — only when the list endpoint sent homework_status (the viewer may edit it) -->
          <div v-if="showHomework && homeworkStatus !== 'none'" class="mt-4 pt-4 border-t border-stroke dark:border-stroke-dark">
            <button @click="showHomeworkModal = true"
                    class="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors"
                    :class="{
                      none: 'border-stroke dark:border-stroke-dark text-fg-base dark:text-fg-base-dark',
                      draft: 'border-amber-400 dark:border-amber-600 text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20',
                      sent: 'border-green-500 dark:border-green-700 text-green-800 dark:text-green-300 bg-green-50 dark:bg-green-900/20',
                    }[homeworkStatus]">
              <span class="flex items-center gap-2">
                <PaperClipIcon class="h-4 w-4" />
                {{ homeworkStatus === 'sent' ? 'Homework sent' : 'Homework draft — not sent' }}
              </span>
              <span class="text-xs opacity-70">Open</span>
            </button>
          </div>

          <!-- Billing -->
          <div v-if="lesson.billing_status"
               class="mt-4 pt-4 border-t border-stroke dark:border-stroke-dark flex items-center justify-between">
            <span class="text-xs text-fg-muted dark:text-fg-muted-dark">Billing</span>
            <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium" :class="billingClass">
              {{ billingLabel }}
            </span>
          </div>

          <!-- Footer actions (closing is the X in the header). Add Homework is the
               primary action when offered; otherwise Edit Lesson is. -->
          <div v-if="isEditable || addHomework" class="mt-5 flex justify-end items-center gap-2">
            <button v-if="isEditable" @click="emit('edit')" data-tour="lesson-details-edit-btn"
                    class="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors"
                    :class="addHomework
                      ? 'border border-stroke dark:border-stroke-dark text-fg-secondary dark:text-fg-secondary-dark'
                      : 'bg-blue-600 text-white'">
              <PencilSquareIcon class="h-4 w-4" />
              Edit Lesson
            </button>
            <button v-if="addHomework" @click="showHomeworkModal = true"
                    class="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white transition-colors">
              <PaperClipIcon class="h-4 w-4" />
              Add Homework
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>

  <HomeworkModal
    v-if="showHomeworkModal"
    :lesson="lesson"
    @status="st => { lesson.homework_status = st }"
    @close="showHomeworkModal = false"
  />
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import {
  XMarkIcon,
  CalendarIcon,
  ClockIcon,
  MusicalNoteIcon,
  UserIcon,
  MapPinIcon,
  PencilSquareIcon,
  ChevronDownIcon,
  PhoneIcon,
  EnvelopeIcon,
  PaperClipIcon,
} from '@heroicons/vue/24/outline'
import HomeworkModal from './HomeworkModal.vue'
import AbsenceCourtesyBadge from './AbsenceCourtesyBadge.vue'
import { avatarGradient } from '@/composables/useAvatarGradient.js'
import { useApi } from '@/composables/useApi.js'
import { notifyLessonsChanged } from '@/composables/useCalendarData.js'

const props = defineProps({
  lesson:     { type: Object,  required: true },
  isEditable: { type: Boolean, default: false },
})

const emit = defineEmits(['close', 'edit'])

const { markLessonAttended, markLessonExcused, markLessonAbsent, markLessonNotReported, fetchApi } = useApi()

// ── Homework ────────────────────────────────────────────────────────────────
// Offered from the lesson's day onward (homework comes out of the lesson; the morning
// of lets a teacher prep files before the student arrives), or any time it exists.
const showHomeworkModal = ref(false)
const homeworkStatus = computed(() => props.lesson.homework_status ?? 'none')
const showHomework = computed(() => {
  if (props.lesson.homework_status === undefined) return false
  if (Array.isArray(props.lesson.attendance) && props.lesson.attendance.length === 0) return false
  if (homeworkStatus.value !== 'none') return true
  const d = props.lesson.lesson_date
  const lessonDay = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(d + 'T00:00:00') : new Date(d)
  const today = new Date(); today.setHours(0, 0, 0, 0)
  return lessonDay <= today
})
const addHomework = computed(() => showHomework.value && homeworkStatus.value === 'none')

// ── Local status overrides (optimistic) ─────────────────────────────────────
const localStatuses = ref({})
const openDropdown  = ref(null)

const students = computed(() =>
  Array.isArray(props.lesson.attendance)
    ? props.lesson.attendance.filter(s => s.name)
    : []
)

const localStatus = (s) =>
  localStatuses.value[s.name] ?? s.status ?? s.attendance ?? null

// ── Primary contact lookup ───────────────────────────────────────────────────
const studentContacts = ref({})  // student name -> { contact_name, contact_email, contact_phone }

const fetchStudentContacts = async () => {
  const names = students.value.map(s => s.name)
  if (!names.length) return
  try {
    const params = names.map(n => `names=${encodeURIComponent(n)}`).join('&')
    const results = await fetchApi(`/api/students/lookup?${params}`)
    studentContacts.value = Object.fromEntries(results.map(r => [r.name, r]))
  } catch {
    studentContacts.value = {}
  }
}
onMounted(fetchStudentContacts)

// ── Absence courtesy (admins only; the endpoint enforces it too) ─────────────
const isAdmin = (() => {
  try { return !!JSON.parse(localStorage.getItem('user') || 'null')?.permissions?.includes('admin') } catch { return false }
})()
const courtesies = ref([])
const fetchCourtesies = async () => {
  if (!isAdmin || !students.value.some(s => localStatus(s) === 'absent')) { courtesies.value = []; return }
  try { courtesies.value = await fetchApi(`/api/absence-courtesies/lesson/${props.lesson.id}`) } catch { courtesies.value = [] }
}
const courtesyFor = (s) => localStatus(s) === 'absent' && courtesies.value.find(c => c.name === s.name)
onMounted(fetchCourtesies)

const primaryContacts = computed(() => {
  const seen = new Map()
  for (const s of students.value) {
    const c = studentContacts.value[s.name]
    if (!c || !c.contact_name) continue
    const key = c.contact_email || c.contact_phone || c.contact_name
    if (!seen.has(key)) seen.set(key, c)
  }
  return [...seen.values()]
})

// ── Dropdown ─────────────────────────────────────────────────────────────────

const isTooFarFuture = computed(() => {
  if (!props.lesson.lesson_date) return false
  const lessonMidnight = new Date(props.lesson.lesson_date + 'T00:00:00')
  return lessonMidnight > new Date(Date.now() + 24 * 60 * 60 * 1000)
})

const ALL_STATUS_OPTIONS = [
  { value: 'attended',     label: 'Attended',     dot: 'bg-green-500' },
  { value: 'excused',      label: 'Excused',       dot: 'bg-yellow-500' },
  { value: 'absent',       label: 'Absent',        dot: 'bg-red-500' },
  { value: null,           label: 'Not Reported',  dot: 'bg-fg-faint dark:bg-fg-faint-dark' },
]

const statusOptions = computed(() =>
  isTooFarFuture.value
    ? ALL_STATUS_OPTIONS.filter(o => o.value !== 'attended' && o.value !== 'absent')
    : ALL_STATUS_OPTIONS
)

const toggleDropdown = (name) => {
  openDropdown.value = openDropdown.value === name ? null : name
}

const closeDropdown = () => { openDropdown.value = null }

// Close on outside click
const onClickOutside = (e) => {
  if (!e.target.closest('[data-attendance-dropdown]')) closeDropdown()
}
onMounted(() => document.addEventListener('click', onClickOutside, true))
onUnmounted(() => document.removeEventListener('click', onClickOutside, true))

const selectStatus = async (studentName, status) => {
  closeDropdown()
  const previous = localStatuses.value[studentName] ?? null
  localStatuses.value = { ...localStatuses.value, [studentName]: status }

  try {
    const apiMap = {
      attended: () => markLessonAttended(props.lesson.id, studentName),
      excused:  () => markLessonExcused(props.lesson.id, studentName),
      absent:   () => markLessonAbsent(props.lesson.id, studentName),
      null:     () => markLessonNotReported(props.lesson.id, studentName),
    }
    await (apiMap[status ?? 'null'] ?? apiMap['null'])()
    notifyLessonsChanged()
    fetchCourtesies()
  } catch {
    // Roll back optimistic update on failure
    localStatuses.value = { ...localStatuses.value, [studentName]: previous }
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const initials = (name) => {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  return parts.length === 1
    ? parts[0][0].toUpperCase()
    : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  const d = /^\d{4}-\d{2}-\d{2}$/.test(dateString)
    ? new Date(dateString + 'T00:00:00')
    : new Date(dateString)
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
}

const formatStatus = (status) => {
  if (!status || status === 'not reported' || status === 'scheduled') return 'Not Reported'
  return { attended: 'Attended', excused: 'Excused', absent: 'Absent' }[status]
    ?? (status.charAt(0).toUpperCase() + status.slice(1))
}

const statusClass = (status) => ({
  attended: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
  excused:  'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200',
  absent:   'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200',
}[status] ?? 'bg-surface-muted dark:bg-surface-muted-dark text-fg-secondary dark:text-fg-secondary-dark')

const billingLabel = computed(() =>
  ({ succeeded: 'Paid', pending: 'Pending', failed: 'Failed', waived: 'Waived' }[props.lesson.billing_status]
    ?? props.lesson.billing_status)
)

const billingClass = computed(() =>
  ({
    succeeded: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
    pending:   'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200',
    failed:    'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200',
    waived:    'bg-surface-muted dark:bg-surface-muted-dark text-fg-muted dark:text-fg-muted-dark',
  }[props.lesson.billing_status] ?? 'bg-surface-muted dark:bg-surface-muted-dark text-fg-secondary dark:text-fg-secondary-dark')
)
</script>
